import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, Sparkles, Check, FileDown, Trash2 } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

export function SvgOptimizer() {
  const [inputSvg, setInputSvg] = useState(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100%" height="100%">
  <!-- Created with Love & Custom Design -->
  <metadata id="metadata-core">
    <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#" xmlns:dc="http://purl.org/dc/elements/1.1/">
      <dc:format>image/svg+xml</dc:format>
    </rdf:RDF>
  </metadata>
  <g id="Layer_1" data-name="Layer 1">
    <rect x="10.0" y="10.0" width="80.0" height="80.0" rx="15.0" fill="#f43f5e" stroke="#000000" stroke-width="2.0000" />
    <circle cx="50.0" cy="50.0" r="20.000" fill="#ffffff" />
  </g>
</svg>`);
  const [outputSvg, setOutputSvg] = useState("");
  const [copied, setCopied] = useState(false);
  const [savings, setSavings] = useState({ original: 0, optimized: 0, percent: 0 });

  // Optimization toggles
  const [options, setOptions] = useState({
    removeComments: true,
    removeMetadata: true,
    removeIdAttributes: false,
    roundDecimals: true,
    decimalPrecision: 2,
    removeEmptyGroups: true,
    collapseWhitespace: true
  });

  const handleToggle = (key: keyof typeof options) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handlePrecisionChange = (val: number) => {
    setOptions(prev => ({ ...prev, decimalPrecision: val }));
  };

  const optimizeSvg = (svg: string) => {
    if (!svg.trim()) {
      setOutputSvg("");
      setSavings({ original: 0, optimized: 0, percent: 0 });
      return;
    }

    let optimized = svg;

    // 1. Remove XML/Doc Declarations
    optimized = optimized.replace(/<\?xml[^>]*\?>/gi, "");
    optimized = optimized.replace(/<!DOCTYPE[^>]*>/gi, "");

    // 2. Remove Comments
    if (options.removeComments) {
      optimized = optimized.replace(/<!--[\s\S]*?-->/g, "");
    }

    // 3. Remove Metadata Elements
    if (options.removeMetadata) {
      optimized = optimized.replace(/<metadata[\s\S]*?<\/metadata>/gi, "");
      optimized = optimized.replace(/<rdf:RDF[\s\S]*?<\/rdf:RDF>/gi, "");
    }

    // 4. Remove empty groups
    if (options.removeEmptyGroups) {
      optimized = optimized.replace(/<g[^>]*>\s*<\/g>/gi, "");
    }

    // 5. Remove useless ID attributes
    if (options.removeIdAttributes) {
      optimized = optimized.replace(/\s+id=["'][^"']*["']/gi, "");
    }

    // 6. Round Decimals
    if (options.roundDecimals) {
      const precision = options.decimalPrecision;
      // Regex matches floating point numbers in attributes
      optimized = optimized.replace(/([= ]"?)(\d+\.\d+)(["' ]?)/g, (match, prefix, numStr, suffix) => {
        const num = parseFloat(numStr);
        if (!isNaN(num)) {
          const rounded = num.toFixed(precision);
          // Remove trailing zeros in decimal representation
          const cleanNum = parseFloat(rounded).toString();
          return `${prefix}${cleanNum}${suffix}`;
        }
        return match;
      });
    }

    // 7. Collapse Whitespace
    if (options.collapseWhitespace) {
      optimized = optimized.replace(/\s+/g, " ");
      optimized = optimized.replace(/>\s+</g, "><");
    }

    optimized = optimized.trim();
    setOutputSvg(optimized);

    const origSize = new Blob([svg]).size;
    const optSize = new Blob([optimized]).size;
    const diff = origSize - optSize;
    const pct = origSize > 0 ? (diff / origSize) * 100 : 0;

    setSavings({
      original: origSize,
      optimized: optSize,
      percent: Math.max(0, parseFloat(pct.toFixed(1)))
    });
  };

  useEffect(() => {
    optimizeSvg(inputSvg);
  }, [inputSvg, options]);

  const handleCopy = () => {
    copyToClipboard(outputSvg).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const downloadSvg = () => {
    const blob = new Blob([outputSvg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "optimized.svg";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ToolPageLayout
      title="SVG Optimizer Online - Optimize SVG Files Free"
      description="Optimize, compress, and simplify vector SVG graphics online. Remove metadata, comments, empty groups, and reduce precision decimals to shrink file sizes up to 80%."
      h1="SVG Optimizer"
      category="SVG Tools"
      categoryPath="/svg-tools"
      howToUse={[
        "Paste your unoptimized SVG raw code into the input field.",
        "Toggle settings in the config panel to customize metadata deletion, comments removal, and precision parameters.",
        "Observe the real-time file size savings and dynamic visual previews.",
        "Copy the cleaned vector graphic or download it as an optimized .svg file."
      ]}
      faq={[
        { question: "What is an online SVG optimizer and how does it shrink files?", answer: "An online SVG optimizer is a utility that parses SVG (Scalable Vector Graphics) source code and strips redundant code, XML markup overhead, designer tool metadata (such as Adobe Illustrator or Inkscape templates), comments, and over-precise floating-point decimals to significantly shrink SVG size while keeping rendering details identical." },
        { question: "Is this SVG optimizer secure for company vector graphics?", answer: "Yes, this tool operates 100% locally in your client browser sandbox. No file data, code snippets, or vector layouts are transmitted to servers, providing absolute security and privacy." },
        { question: "Why should I optimize SVGs for website design?", answer: "Unoptimized SVGs often contain up to 70% bloat from editing tools. Optimizing vector files speeds up page loading times, decreases mobile bandwidth consumption, and boosts Google PageSpeed Insights and Core Web Vitals rankings." },
        { question: "What does rounding decimals in an SVG do?", answer: "Vector editors export paths with excessive floating-point precision (e.g., width='10.000034'). By rounding decimals to 2 or 1 digits, the coordinate strings are shortened, dramatically reducing size with no noticeable impact on visual rendering." },
        { question: "Is SVG optimization better than PNG/WebP compression?", answer: "Yes! SVGs are code-based vector documents, meaning they can be compressed by stripping code characters. PNG and WebP are pixel-based, so they compress by degrading image quality. SVGs remain fully scalable and infinitely sharp." },
        { question: "What editor metadata gets removed during optimization?", answer: "Standard editors like Sketch or Illustrator append bulky namespaces, structural editor tags, creator profiles, previews, and empty layers. This tool strips RDF, Dublin Core XML fields, and editor layers." },
        { question: "Can optimized SVGs still be styled with CSS and manipulated with JavaScript?", answer: "Absolutely. As long as you do not strip essential IDs or classes used by your codebase (which is customizable in our settings), the optimized SVG remains valid HTML-friendly code and is fully scriptable." },
        { question: "Does optimizing an SVG reduce its visual quality?", answer: "No. Unless you set the decimal precision too low (like 0 decimals on complex paths), the optimized SVG will look completely identical to the original version at any zoom level." },
        { question: "What is SVGO and how does this tool compare to it?", answer: "SVGO (SVG Optimizer) is a powerful Node.js command-line interface. This online browser utility provides a fast, visual, non-technical alternative to SVGO with intuitive toggle buttons, size counters, and live preview rendering." },
        { question: "How do I embed optimized SVGs into HTML securely?", answer: "Optimized SVGs can be securely embedded directly inline using the `<svg>` tag, or references via `<img>` tags (using safe Referrer Policies), CSS background-image declarations, or as reusable web component imports." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Controls Config Column */}
          <div className="space-y-4 bg-white border border-slate-200 p-5 rounded-2xl shadow-xs">
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100 text-rose-600">
              <Sparkles className="w-5 h-5" />
              <span className="font-bold text-sm uppercase tracking-wider">Optimizer Options</span>
            </div>

            <div className="space-y-3">
              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.removeComments}
                  onChange={() => handleToggle("removeComments")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Remove Comments {"(<!-- -->)"}
              </label>

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.removeMetadata}
                  onChange={() => handleToggle("removeMetadata")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Remove XML Metadata & RDFs
              </label>

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.removeEmptyGroups}
                  onChange={() => handleToggle("removeEmptyGroups")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Remove Empty Groups (&lt;g&gt;)
              </label>

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.removeIdAttributes}
                  onChange={() => handleToggle("removeIdAttributes")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Strip ID attributes
              </label>

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.roundDecimals}
                  onChange={() => handleToggle("roundDecimals")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Round Floats / Coordinates
              </label>

              {options.roundDecimals && (
                <div className="pl-7 space-y-1">
                  <Label className="text-[10px] text-slate-500 font-bold uppercase">Decimal Precision</Label>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4].map(num => (
                      <button
                        key={num}
                        onClick={() => handlePrecisionChange(num)}
                        className={`px-2 py-1 text-xs rounded border transition-colors ${
                          options.decimalPrecision === num
                            ? "bg-rose-50 border-rose-200 text-rose-700 font-bold"
                            : "bg-white border-slate-200 text-slate-600"
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <label className="flex items-center gap-3 text-xs font-semibold text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.collapseWhitespace}
                  onChange={() => handleToggle("collapseWhitespace")}
                  className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 w-4 h-4"
                />
                Minify & Collapse Whitespace
              </label>
            </div>

            {/* Savings dashboard */}
            {savings.original > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2 mt-4">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Optimization Report</span>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 pt-1">
                  <div>Original: <strong className="font-semibold text-slate-800">{savings.original} B</strong></div>
                  <div>Optimized: <strong className="font-semibold text-slate-800">{savings.optimized} B</strong></div>
                </div>
                {savings.percent > 0 && (
                  <div className="bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-lg p-2 text-center text-xs font-bold mt-1">
                    🎉 Shrunk by {savings.percent}%
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Code Input & Output Fields */}
          <div className="md:col-span-2 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Raw SVG Input</Label>
                  <button
                    onClick={() => setInputSvg("")}
                    className="text-[10px] text-slate-400 hover:text-red-500 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Clear
                  </button>
                </div>
                <Textarea
                  value={inputSvg}
                  onChange={(e) => setInputSvg(e.target.value)}
                  className="font-mono text-[10px] leading-relaxed min-h-[300px] bg-slate-50"
                  placeholder="Paste unoptimized SVG code here..."
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Optimized SVG Output</Label>
                <Textarea
                  value={outputSvg}
                  readOnly
                  className="font-mono text-[10px] leading-relaxed min-h-[300px] bg-white border-emerald-200 focus-visible:ring-emerald-500"
                  placeholder="Optimized code outputs automatically..."
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleCopy}
                disabled={!outputSvg}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white gap-2"
              >
                <Copy className="w-4 h-4" />
                {copied ? "Copied Code!" : "Copy Cleaned SVG Code"}
              </Button>
              <Button
                onClick={downloadSvg}
                disabled={!outputSvg}
                variant="outline"
                className="gap-2 text-slate-700 border-slate-300"
              >
                <FileDown className="w-4 h-4" />
                Download File
              </Button>
            </div>
          </div>
        </div>

        {/* Live render preview block */}
        {outputSvg && (
          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 space-y-3">
            <h4 className="font-bold text-sm uppercase tracking-wider text-slate-800">Visual Quality Verification</h4>
            <div className="flex justify-center items-center min-h-[180px] bg-white border border-slate-200 rounded-xl p-4 overflow-hidden relative checkerboard-bg">
              <div
                className="max-w-[150px] max-h-[150px]"
                dangerouslySetInnerHTML={{ __html: outputSvg }}
              />
            </div>
            <p className="text-[11px] text-slate-500 text-center leading-relaxed">
              If the rendered design looks identical to your original layout, the optimization is 100% successful and safe for web servers.
            </p>
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}
